import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Confirm } from '@/components/ui/Confirm';
import { Pill } from '@/components/ui/Pill';
import { useSession } from '@/lib/auth/useSession';
import { formatDateTime } from '@/lib/format';
import { MIN_PASSWORD_LENGTH, type VerifiedDevice } from './api';
import { useDevices, useInvalidateDevice, useSetPassword, useUpdateName } from './queries';

/**
 * Settings (from the avatar menu): the signed-in user's own profile name,
 * password and verified devices.
 */
export function SettingsScreen() {
  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-2xl px-6 py-8">
        <h1 className="text-xl font-semibold text-ink">Settings</h1>
        <ProfileSection />
        <SecuritySection />
        <DevicesSection />
        <p className="mt-6 text-right text-[11px] text-ink-faint" aria-label="App version">
          v{__APP_VERSION__}
        </p>
      </div>
    </div>
  );
}

function Section({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <section className="mt-6 rounded-xl border border-line bg-surface p-5">
      <h2 className="text-sm font-semibold uppercase tracking-wider text-ink-soft">{title}</h2>
      {hint && <p className="mt-1 text-xs text-ink-faint">{hint}</p>}
      <div className="mt-4">{children}</div>
    </section>
  );
}

const input =
  'mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-sage disabled:bg-surface-alt disabled:text-ink-faint';

function ProfileSection() {
  const { me } = useSession();
  const save = useUpdateName();
  const [firstName, setFirstName] = useState(me?.profile.firstName ?? '');
  const [lastName, setLastName] = useState(me?.profile.lastName ?? '');
  useEffect(() => {
    setFirstName(me?.profile.firstName ?? '');
    setLastName(me?.profile.lastName ?? '');
  }, [me?.profile.firstName, me?.profile.lastName]);

  const dirty = firstName.trim() !== (me?.profile.firstName ?? '') || lastName.trim() !== (me?.profile.lastName ?? '');
  if (!me) return null;

  return (
    <Section title="Profile">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="text-sm font-semibold text-ink">
          First name
          <input value={firstName} onChange={(e) => setFirstName(e.target.value)} className={input} />
        </label>
        <label className="text-sm font-semibold text-ink">
          Last name
          <input value={lastName} onChange={(e) => setLastName(e.target.value)} className={input} />
        </label>
      </div>
      <label className="mt-3 block text-sm font-semibold text-ink">
        Email
        <input value={me.profile.email ?? ''} disabled className={input} aria-describedby="email-hint" />
      </label>
      <p id="email-hint" className="mt-1 text-xs text-ink-faint">
        Your sign-in email. Ask DevOps to change it.
      </p>
      <div className="mt-4 flex items-center justify-end gap-3">
        {save.isSuccess && !dirty && <span className="text-xs text-sage-dark">Saved.</span>}
        <Button
          disabled={!dirty || !firstName.trim() || save.isPending}
          onClick={() => save.mutate({ profileId: me.profile.id, firstName: firstName.trim(), lastName: lastName.trim() })}
        >
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </Section>
  );
}

function SecuritySection() {
  const { me } = useSession();
  const save = useSetPassword();
  const [password, setPasswordValue] = useState('');
  const [confirm, setConfirm] = useState('');
  const tooShort = password.length > 0 && password.length < MIN_PASSWORD_LENGTH;
  const mismatch = confirm.length > 0 && confirm !== password;
  const ready = password.length >= MIN_PASSWORD_LENGTH && confirm === password;
  if (!me) return null;

  return (
    <Section title="Security" hint="Set or change the password you sign in with. You can always sign in with an emailed code instead.">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!ready) return;
          save.mutate(
            { userId: me.userId, password },
            {
              onSuccess: () => {
                setPasswordValue('');
                setConfirm('');
              },
            },
          );
        }}
        noValidate
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="text-sm font-semibold text-ink">
            New password
            <input
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPasswordValue(e.target.value)}
              className={input}
            />
          </label>
          <label className="text-sm font-semibold text-ink">
            Confirm new password
            <input
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className={input}
            />
          </label>
        </div>
        <p className={`mt-2 text-xs ${tooShort || mismatch ? 'text-bronze' : 'text-ink-faint'}`}>
          {mismatch ? 'The passwords don’t match.' : `At least ${MIN_PASSWORD_LENGTH} characters.`}
        </p>
        <div className="mt-4 flex items-center justify-end gap-3">
          {save.isSuccess && <span className="text-xs text-sage-dark">Password updated.</span>}
          <Button type="submit" disabled={!ready || save.isPending}>
            {save.isPending ? 'Saving…' : 'Set password'}
          </Button>
        </div>
      </form>
    </Section>
  );
}

function DevicesSection() {
  const { data: devices, isLoading } = useDevices();
  const verified = (devices ?? [])
    .filter((d) => d.mfaVerifiedAt)
    .sort((a, b) => Number(b.current) - Number(a.current) || (b.lastAuthentication ?? '').localeCompare(a.lastAuthentication ?? ''));

  return (
    <Section
      title="Verified devices"
      hint="Devices where you've entered an emailed code in the last two weeks. Removing one means it needs a new code to sign in."
    >
      {isLoading ? (
        <p className="text-sm text-ink-faint">Loading…</p>
      ) : verified.length === 0 ? (
        <p className="text-sm text-ink-faint">No verified devices.</p>
      ) : (
        <ul className="divide-y divide-line-soft">
          {verified.map((d) => (
            <DeviceRow key={d.id} device={d} />
          ))}
        </ul>
      )}
    </Section>
  );
}

function DeviceRow({ device: d }: { device: VerifiedDevice }) {
  const remove = useInvalidateDevice();
  const [confirming, setConfirming] = useState(false);
  return (
    <li className="relative flex items-center gap-3 py-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 text-sm font-semibold text-ink">
          {d.name || d.platform || 'Unknown device'}
          {d.current && <Pill tone="sage">This device</Pill>}
        </div>
        <div className="mt-0.5 text-xs text-ink-faint">
          Last sign-in {formatDateTime(d.lastAuthentication) || '—'} · Verified {formatDateTime(d.mfaVerifiedAt)}
        </div>
      </div>
      {!d.current && (
        <Button variant="secondary" disabled={remove.isPending} onClick={() => setConfirming(true)}>
          Remove
        </Button>
      )}
      {confirming && (
        <Confirm
          title="Remove this device?"
          body="It will need an emailed code the next time it signs in."
          confirmLabel="Remove"
          busy={remove.isPending}
          onCancel={() => setConfirming(false)}
          onConfirm={() => remove.mutate(d.id, { onSettled: () => setConfirming(false) })}
        />
      )}
    </li>
  );
}
